import moment from "moment";
import { useTranslation } from "react-i18next";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import { Dropdown, Modal } from "antd";
import {
  DeleteOutlined,
  EllipsisOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useEffect, useState, useCallback, useRef } from "react";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import { Chips } from "../../../../components/chips/chips";
import AiDraftLabel from "../../../../components/table/ai-draft-label";
import BulkSelectionBar from "../../../../components/table/bulk-selection-bar";
import { useBulkSelection } from "../../../../common/utils/useBulkSelection";

const DeleteModalComponent = ({ plan, handleOk, handleCancel, isModalOpen }) => (
  <Modal
    title={plan?.title}
    open={isModalOpen}
    onOk={handleOk}
    onCancel={handleCancel}
    okButtonProps={{ danger: true }}
    className="plan-page-action-modal"
  >
    <p>Are you sure you want to delete plan?</p>
  </Modal>
);

const DropdownActionItem = ({ remove, duplicate }) => {
  const items = [
    { key: "1", label: "Duplicate", icon: <CopyOutlined />, onClick: () => duplicate() },
    { key: "2", label: "Delete", icon: <DeleteOutlined />, onClick: () => remove() },
  ];
  return (
    <Dropdown
      menu={{ items, onClick: (e) => e.domEvent.stopPropagation() }}
      trigger={["click"]}
      styles={{ root: { width: 120 } }}
    >
      <EllipsisOutlined
        style={{ cursor: "pointer", width: 30 }}
        onClick={(e) => e.stopPropagation()}
      />
    </Dropdown>
  );
};

const PlanTable = ({ data, onHandleRowClick, removePlan, duplicatePlan, isLoading, hasMore, fetchData, onBulkDelete }) => {
  const [isModalOpen, setModalOpen] = useState();
  const [item, setItem] = useState();
  const [sortedData, setSortedData] = useState(data);
  const [sortInfo, setSortInfo] = useState({});
  const { t } = useTranslation();
  const [isBulkDeleting, setBulkDeleting] = useState(false);

  const handleOpenDeleteModal = (plan) => { setModalOpen(true); setItem(plan); };
  const handleCloseDeleteModal = () => { setModalOpen(false); setItem(); };
  const handleOkPress = (id, planTitle) => { handleCloseDeleteModal(); removePlan(id, planTitle); };
  const handleDuplicate = (id) => duplicatePlan(id);

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const {
    selectedKeys,
    clearSelection,
    getSelectionColumn,
    setSelectedKeys,
  } = useBulkSelection(data, { rowKey: "id" });

  const handleBulkDeleteClick = useCallback(() => {
    if (!onBulkDelete || selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    onBulkDelete(selectedKeys, {
      onDone: ({ failedIds = [] } = {}) => {
        if (!failedIds.length) {
          clearSelection();
        } else {
          setSelectedKeys(failedIds);
        }

        setBulkDeleting(false);
      },
    });
  }, [
    onBulkDelete,
    selectedKeys,
    isBulkDeleting,
    clearSelection,
    setSelectedKeys,
  ]);

  useEffect(() => { setSortedData(data); }, [data]);

  const getCohortNames = (plan) =>
    (plan.cohorts || [])
      .map((cohort) => cohort.name)
      .filter(Boolean)
      .join(", ");

  const getCohortString = (plan) => getCohortNames(plan) || "N/A";

  const handleTableChange = (pagination, filters, sorter) => {
    if (!sorter.order) { setSortedData(data); setSortInfo({}); return; }
    const sorted = [...data].sort((a, b) => {
      const aVal = a[sorter.field];
      const bVal = b[sorter.field];

      if (sorter.field === 'updatedAt') {
        return sorter.order === 'ascend'
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }
      if (sorter.field === "cohort") {
        const aText = getCohortString(a).toLowerCase();
        const bText = getCohortString(b).toLowerCase();
        return sorter.order === "ascend" ? aText.localeCompare(bText) : bText.localeCompare(aText);
      }
      if (typeof aVal === 'string') {
        return sorter.order === 'ascend' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return 0;
    });
    setSortedData(sorted);
    setSortInfo(sorter);
  };

  const columns = [
    getSelectionColumn(),
    {
      title: t("plan_management.table.name"),
      dataIndex: "title",
      render: (_, plan) => (
        <div className="tasks-table__name-container">
          <span style={{ fontWeight: 700 }}>{plan.title}</span>
          <AiDraftLabel record={plan} />
        </div>
      ),
      key: "title",
      width: "20%",
      sortOrder: sortInfo.field === 'title' ? sortInfo.order : null,
      sorter: true
    },
    {
      title: t("plan_management.table.cohort"),
      dataIndex: "cohort",
      render: (_, plan) => {
        const cohortNames = getCohortNames(plan);

        return cohortNames ? (
          <span>{cohortNames}</span>
        ) : (
          <Chips
            text={t("plan_management.table.cohort_not_assigned")}
            color="grey"
          />
        );
      },
      key: "cohort",
      width: "20%",
      sortOrder: sortInfo.field === 'cohort' ? sortInfo.order : null,
      sorter: true
    },
    {
      title: t("plan_management.table.status"),
      dataIndex: "status",
      render: (_, plan) => <UserActivityStatus userStatus={plan.status} />,
      key: "status",
      width: "20%",
      sortOrder: sortInfo.field === 'status' ? sortInfo.order : null,
      sorter: true
    },
    {
      title: t("task_management.table.last_modified"),
      dataIndex: "updatedAt",
      render: (_, plan) => (
        <span>
          {`${moment(plan.updatedAt).utc().format("DD MMM YYYY, HH:mm")}, UTC` || "N/A"}
        </span>
      ),
      key: "updatedAt",
      width: "20%",
      sortOrder: sortInfo.field === 'updatedAt' ? sortInfo.order : null,
      sorter: true
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, plan) => (
        <DropdownActionItem
          remove={() => handleOpenDeleteModal(plan)}
          duplicate={() => handleDuplicate(plan.id)}
        />
      ),
      key: "action",
      width: "10%",
    },
  ];

  return (
    <>
      <DeleteModalComponent
        plan={item}
        handleOk={() => handleOkPress(item.id, item.title)}
        handleCancel={handleCloseDeleteModal}
        isModalOpen={isModalOpen}
      />
      <InfinityTable
        className="lf-bulk-table"
        data={sortedData}
        fetchData={fetchData}
        hasMore={hasMore}
        tableColumns={columns}
        onHandleRowClick={onHandleRowClick}
        onChange={handleTableChange}
        isLoading={isLoading}
        containerRef={tableRef}
        after={
          <BulkSelectionBar
            left={bulkLeft}
            count={selectedKeys.length}
            onClear={clearSelection}
            onDelete={handleBulkDeleteClick}
            loading={isBulkDeleting}
          />
        }
      />
    </>
  );
};

export default PlanTable;
